import { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import {
  INTL_DMY_CASUAL_FORMAT,
  INTL_DAY_MONTH_NAME,
} from 'constants/dateFormats'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { formatPreciseAmountWithDollarType } from 'lib/format/formatCurrencyIntl'
import BedBankBookingCard from 'tripPlanner/components/Bookings/BedBankBookingCard'
import ExperienceBookingCard from 'tripPlanner/components/Bookings/ExperienceBookingCard'
import FlightBookingCard from 'tripPlanner/components/Bookings/FlightBookingCard'
import HotelBookingCard from 'tripPlanner/components/Bookings/HotelBookingCard'
import TourV2BookingCard from 'tripPlanner/components/Bookings/TourV2BookingCard'
import { extractOfferIdsFromOrder } from 'tripPlanner/components/EntryPoints/utils'
import { useExperiences } from 'tripPlanner/hooks/api/offer'
import { useFlightDetailsForItems } from 'tripPlanner/hooks/api/flights'
import type { OfferTypeForTripItemTypes } from 'tripPlanner/hooks/api/common'
import { concatStringWithDot } from 'tripPlanner/utils'
import { extractDestinationFromOrder } from 'tripPlanner/utils/destination'
import { extractOrderStartAndEndDate } from 'tripPlanner/utils/order'
import useOffers from 'hooks/Offers/useOffers'
import PackageBookingItem from '../ItemCard/PackageBookingItem'

type Props = {
  order: App.Order
  onSelect: (
    order: App.Order,
    offers: Array<OfferTypeForTripItemTypes>,
    checked: boolean,
  ) => void
}

function MultiTypeItemBookingCard(props: Props) {
  const { order, onSelect } = props
  const [flights, setFlights] = useState<Array<App.JourneyV2>>([])
  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()

  const [offers, isOffersLoading] = useOffers(extractOfferIdsFromOrder(order))

  const { data: flightDetails, isLoading: isFlightDetailsLoading } =
    useFlightDetailsForItems({
      itemIds: order.flightItems.map((flightItem) => flightItem.id),
      orderId: order.id,
    })

  useEffect(() => {
    if (flightDetails && Array.isArray(flightDetails)) {
      const flightJourneys = flightDetails.map(
        (flightsDetail) => flightsDetail.journeyFares.fares,
      )

      if (flightJourneys.length > 0) {
        setFlights(flightJourneys.flat())
      } else {
        setFlights([])
      }
    }
  }, [flightDetails, setFlights])

  const { data: experiences, isLoading: isExperienceLoading } = useExperiences({
    experienceIds: order.experienceItems.map((item) => item.experienceId),
    currentCurrency: order.currencyCode,
    currentRegionCode: order.regionCode,
  })

  const destinationPackageName = useMemo(() => {
    const computedDestination = extractDestinationFromOrder(
      order,
      offers,
      flights,
      experiences,
    )

    return computedDestination ?? ''
  }, [order, offers, flights, experiences])

  const { startDate, endDate } = useMemo(() => {
    return extractOrderStartAndEndDate(order)
  }, [order])

  const formattedStartDate = startDate ?
      intlDateFormatter(startDate, INTL_DAY_MONTH_NAME) :
    ''
  const formattedEndDate = endDate ?
      intlDateFormatter(endDate, INTL_DMY_CASUAL_FORMAT) :
    ''
  const bookingDateRange =
    formattedStartDate && formattedEndDate ?
      `${formattedStartDate} – ${formattedEndDate}` :
      formattedStartDate || formattedEndDate
  const packageHeading = destinationPackageName ?
      intl.formatMessage(
        {
          id: 'oWNxhTHd6BDGmOaa0zWw',
          defaultMessage: '{destinationName} Package',
          description:
          'Package booking card heading showing the destination name followed by the word Package',
        },
        { destinationName: destinationPackageName },
      ) :
      intl.formatMessage({
        id: 'E_cqb-NS5zAC8Ap67pd0',
        defaultMessage: 'Package',
        description: 'Fallback package booking card heading when no destination name is available',
      })

  const isLoading =
    isExperienceLoading || isOffersLoading || isFlightDetailsLoading

  if (isLoading) {
    return <LoadingIndicator inline />
  }

  return (
    <PackageBookingItem
      onSelect={(checked) => {
        onSelect(order, offers, checked)
      }}
      heading={packageHeading}
      subHeading={concatStringWithDot(
        bookingDateRange,
        formatPreciseAmountWithDollarType(
          order.total,
          order.currencyCode,
          order.regionCode,
        ),
      )}
    >
      {order.bedbankItems.length > 0 &&
        order.bedbankItems.map((orderBedBankItem: App.OrderBedbankItem) => (
          <BedBankBookingCard
            key={orderBedBankItem.id}
            bedbankOrderItem={orderBedBankItem}
            showBorder={false}
          />
        ))}
      {order.items.length > 0 &&
        order.items.map((orderItem: App.OrderItem) => (
          <HotelBookingCard
            key={orderItem.id}
            order={order}
            orderItem={orderItem}
            showBorder={false}
          />
        ))}
      {order.flightItems.length > 0 &&
        order.flightItems.map((orderFlightItem: App.OrderFlightItem) => (
          <FlightBookingCard
            key={orderFlightItem.id}
            order={order}
            orderFlightItem={orderFlightItem}
            showBorder={false}
          />
        ))}
      {order.tourItems.length > 0 &&
        order.tourItems.map((tourV2OrderItem: App.Tours.TourV2OrderItem) => (
          <TourV2BookingCard
            key={tourV2OrderItem.id}
            order={order}
            tourV2OrderItem={tourV2OrderItem}
          />
        ))}
      {order.experienceItems.length > 0 &&
        order.experienceItems.map(
          (orderExperienceItem: App.OrderExperienceItem) => (
            <ExperienceBookingCard
              key={orderExperienceItem.id}
              order={order}
              experienceItem={orderExperienceItem}
              showBorder={false}
            />
          ),
        )}
    </PackageBookingItem>
  )
}

export default MultiTypeItemBookingCard
